import React, { useState, useCallback } from 'react';
import axiosInstance from '../../axiosInstance';
import { debounce } from 'lodash';

const API_BASE = '/redmine-portal';

function SearchIssues({ onSelectIssue, getStatusColor, getPriorityIcon }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searched, setSearched] = useState(false);

  // Debounced search function
  const doSearch = useCallback(
    debounce(async (searchQuery) => {
      if (!searchQuery || searchQuery.length < 2) {
        setResults([]);
        setSearched(false);
        return;
      }

      setLoading(true);
      setError(null);
      setSearched(true);

      try {
        const res = await axiosInstance.get(`${API_BASE}/search`, {
          params: { q: searchQuery, limit: 50 }
        });
        setResults(res.data.issues || []);
      } catch (err) {
        setError(err.response?.data?.error || 'Search failed');
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300),
    []
  );

  const handleQueryChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    doSearch(value);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    doSearch.flush();
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="search-issues">
      <h2>Search Issues</h2>

      <form onSubmit={handleSubmit} className="search-form">
        <input
          type="text"
          value={query}
          onChange={handleQueryChange}
          placeholder="Search by subject..."
          autoFocus
        />
        <button type="submit" disabled={loading}>
          {loading ? 'Searching...' : 'Search'}
        </button>
      </form>

      {error && <p className="search-error">{error}</p>}

      <div className="search-results">
        {loading && (
          <div className="search-loading">
            <div className="spinner"></div>
            <p>Searching...</p>
          </div>
        )}

        {!loading && searched && results.length === 0 && (
          <div className="search-empty">
            <p>No issues found matching "{query}"</p>
          </div>
        )}

        {!loading && results.length > 0 && (
          <>
            <p className="results-count">{results.length} results</p>
            <div className="results-list">
              {results.map(issue => {
                const priority = getPriorityIcon(issue.priority?.name);
                return (
                  <div
                    key={issue.id}
                    className="search-result-item"
                    onClick={() => onSelectIssue(issue.id)}
                  >
                    <div className="result-header">
                      <span className="result-id">#{issue.id}</span>
                      <span
                        className="result-status"
                        style={{ backgroundColor: getStatusColor(issue.status?.name) }}
                      >
                        {issue.status?.name}
                      </span>
                      {priority.icon && (
                        <span className="result-priority" style={{ color: priority.color }}>
                          {priority.icon}
                        </span>
                      )}
                    </div>
                    <div className="result-subject">{issue.subject}</div>
                    <div className="result-meta">
                      <span className="result-project">{issue.project?.name}</span>
                      <span className="result-date">{formatDate(issue.updated_on)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default SearchIssues;
